import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { InboxStackIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { SubPageHeader } from '@/components/shared';
import { FormInput } from '@/components/FormInput';
import { FormSelect } from '@/components/FormSelect';
import { DpQueueList } from '../../components/DpQueueList';
import { DpRequestDrawer } from '../../components/DpRequestDrawer';
import { DpDocumentsPanel } from '../../components/DpDocumentsPanel';
import { useDpQueueQuery } from '../../hooks/data/useDpRequestQueries';
import { useAuth } from '@/contexts/AuthContext';
import { classifyDpRequest, matchesDpQueueFilter, sortByOldestPending, type DpQueueFilterType } from '../../utils/dpQueue';
import { DP_QUEUE_TAB_LABELS, DP_QUEUE_TABS, type DpQueueTab } from '../../dpRequests.definitions';
import { DP_PORTAL_PATH } from '../../routes';
import type { DpRequest } from '../../../../../shared/dpRequests';

const REQUEST_ID_PARAM = 'requestId';

const TYPE_OPTIONS = [
    { value: 'all', label: 'Todos os tipos' },
    { value: 'data_update', label: 'Atualização de dados' },
    { value: 'ticket', label: 'Outros assuntos' },
    { value: 'day_off', label: 'Day-off' },
    { value: 'overtime', label: 'Hora extra' },
    { value: 'raro_invest', label: 'Raro Invest' },
    { value: 'health_plan', label: 'Plano de saúde' },
];

const EMPTY_TEXT: Record<DpQueueTab, string> = {
    pending: 'Nada pendente. Tudo em dia.',
    waiting: 'Nenhum pedido aguardando o colaborador.',
    closed: 'Nenhum pedido concluído nas últimas entradas.',
};

export function DpAttendancePage() {
    const { user } = useAuth();
    const queue = useDpQueueQuery(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const openRequestId = searchParams.get(REQUEST_ID_PARAM);
    const [tab, setTab] = useState<DpQueueTab>('pending');
    const [view, setView] = useState<'queue' | 'documents'>('queue');
    const [type, setType] = useState<DpQueueFilterType>('all');
    const [search, setSearch] = useState('');

    const counts = useMemo(() => {
        const result: Record<DpQueueTab, number> = { pending: 0, waiting: 0, closed: 0 };
        for (const request of queue.data ?? []) result[classifyDpRequest(request)] += 1;
        return result;
    }, [queue.data]);

    const visible = useMemo<DpRequest[]>(() => {
        const filtered = (queue.data ?? []).filter(
            request => classifyDpRequest(request) === tab && matchesDpQueueFilter(request, type, search)
        );
        return tab === 'closed' ? filtered : sortByOldestPending(filtered);
    }, [queue.data, tab, type, search]);

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo={DP_PORTAL_PATH}
                icon={<InboxStackIcon className="h-6 w-6" aria-hidden="true" />}
                title="Atendimentos"
                description="Pedidos dos colaboradores para o DP. Os mais antigos aparecem primeiro."
            />

            <div className="flex gap-2" role="tablist" aria-label="Visão do DP">
                {(['queue', 'documents'] as const).map(item => (
                    <button
                        key={item}
                        type="button"
                        role="tab"
                        aria-selected={view === item}
                        onClick={() => setView(item)}
                        className={clsx(
                            'rounded-xl px-3.5 py-2 text-sm font-semibold transition',
                            view === item ? 'bg-raro-gray-900 text-white' : 'text-raro-gray-600 hover:bg-raro-gray-100'
                        )}
                    >
                        {item === 'queue' ? 'Fila' : 'Documentos'}
                    </button>
                ))}
            </div>

            {view === 'documents' && (
                queue.isPending ? (
                    <div className="h-48 animate-pulse rounded-2xl bg-raro-gray-100" />
                ) : (
                    <DpDocumentsPanel requests={queue.data ?? []} emptyText="Nenhum documento enviado ainda." />
                )
            )}

            {view === 'queue' && (
                <>
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Situação dos pedidos">
                        {DP_QUEUE_TABS.map(item => (
                            <button
                                key={item}
                                type="button"
                                role="tab"
                                aria-selected={tab === item}
                                onClick={() => setTab(item)}
                                className={clsx(
                                    'inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold transition',
                                    tab === item ? 'bg-primary-600 text-white shadow-sm' : 'bg-white text-raro-gray-600 ring-1 ring-inset ring-raro-gray-200 hover:bg-raro-gray-50'
                                )}
                            >
                                {DP_QUEUE_TAB_LABELS[item]}
                                <span className={clsx('rounded-full px-2 py-0.5 text-xs', tab === item ? 'bg-white/20' : 'bg-raro-gray-100 text-raro-gray-600')}>
                                    {counts[item]}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,240px)]">
                        <FormInput
                            name="search"
                            placeholder="Buscar por nome ou e-mail"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            icon={<MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />}
                        />
                        <FormSelect
                            name="type"
                            value={type}
                            onChange={event => setType(event.target.value as DpQueueFilterType)}
                            options={TYPE_OPTIONS}
                        />
                    </div>

                    {queue.isPending && <div className="h-48 animate-pulse rounded-2xl bg-raro-gray-100" />}
                    {queue.isError && (
                        <p className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                            Não foi possível carregar os atendimentos.
                        </p>
                    )}
                    {queue.data && <DpQueueList requests={visible} emptyText={EMPTY_TEXT[tab]} onOpen={id => setSearchParams({ [REQUEST_ID_PARAM]: id })} />}
                </>
            )}

            <DpRequestDrawer
                requestId={openRequestId}
                dpEmail={user?.email ?? ''}
                onClose={() => setSearchParams({})}
            />
        </div>
    );
}

export default DpAttendancePage;
